import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
    Users as UsersIcon,
    Plus,
    Search,
    Edit,
    Trash2,
    Eye,
    MapPin,
    Camera,
    X,
    Shield,
    Mail
} from 'lucide-react'
import api from '../services/api'
import UserModal from '../components/UserModal'
import UserDetailsModal from '../components/UserDetailsModal'
import UserDeletionLogs from '../components/UserDeletionLogs'


const Users = () => {
    const { hasPermission, user: currentUser, isSuperAdmin } = useAuth()
    const [users, setUsers] = useState([])
    const [roles, setRoles] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [selectedUser, setSelectedUser] = useState(null)
    const [showUserModal, setShowUserModal] = useState(false)
    const [showUserDetailsModal, setShowUserDetailsModal] = useState(false)
    const [showLocationModal, setShowLocationModal] = useState(false)
    const [selectedUserLocations, setSelectedUserLocations] = useState([])
    const [showOTPModal, setShowOTPModal] = useState(false)
    const [otp, setOtp] = useState('')
    const [otpLoading, setOtpLoading] = useState(false)
    const [otpError, setOtpError] = useState('')
    const [userToDelete, setUserToDelete] = useState(null)
    const [requestingOTP, setRequestingOTP] = useState(false)

    const [editingUser, setEditingUser] = useState(null)

    useEffect(() => {
        fetchUsers()
        if (hasPermission('roles', 'read')) {
            fetchRoles()
        }
    }, [currentPage, searchTerm])

    const fetchUsers = async () => {
        try {
            const params = new URLSearchParams({
                page: currentPage,
                limit: 10,
                ...(searchTerm && { search: searchTerm })
            })

            const response = await api.get(`/users/access-stats?${params}`)
            setUsers(response.data.data.users)
            setTotalPages(response.data.data.pagination.pages)
        } catch (error) {
            console.error('Error fetching users:', error)
        } finally {
            setLoading(false)
        }
    }

    const fetchRoles = async () => {
        try {
            const response = await api.get('/roles')
            setRoles(response.data.data.roles)
        } catch (error) {
            console.error('Error fetching roles:', error)
        }
    }

    const handleSearch = (e) => {
        e.preventDefault()
        setCurrentPage(1)
        fetchUsers()
    }

    const toggleUserStatus = async (userId) => {
        try {
            await api.patch(`/users/${userId}/toggle-status`)
            fetchUsers()
        } catch (error) {
            console.error('Error toggling user status:', error)
        }
    }

    const deleteUser = async (userId) => {
        // Show OTP modal for all users with delete permission
        setUserToDelete(userId)
        setShowOTPModal(true)
        setOtp('')
        setOtpError('')
        // Request OTP automatically
        requestDeletionOTP(userId)
    }

    const requestDeletionOTP = async (userId) => {
        setRequestingOTP(true)
        setOtpError('')
        try {
            await api.post(`/users/${userId}/request-deletion-otp`)
            // OTP sent successfully, modal is already open
        } catch (error) {
            console.error('Error requesting deletion OTP:', error)
            setOtpError(error.response?.data?.message || 'Failed to send OTP. Please try again.')
            // Close modal if OTP request fails
            if (error.response?.status === 403) {
                setTimeout(() => {
                    handleCloseOTPModal()
                }, 2000)
            }
        } finally {
            setRequestingOTP(false)
        }
    }

    const handleOTPSubmit = async (e) => {
        e.preventDefault()
        if (!otp || otp.length !== 6) {
            setOtpError('Please enter a valid 6-digit OTP')
            return
        }

        setOtpLoading(true)
        setOtpError('')

        try {
            await api.delete(`/users/${userToDelete}`, {
                data: { otp }
            })
            // Success
            setShowOTPModal(false)
            setOtp('')
            setUserToDelete(null)
            fetchUsers()
        } catch (error) {
            console.error('Error deleting user with OTP:', error)
            setOtpError(error.response?.data?.message || 'Invalid OTP or error deleting user')
        } finally {
            setOtpLoading(false)
        }
    }

    const handleCloseOTPModal = () => {
        setShowOTPModal(false)
        setOtp('')
        setOtpError('')
        setUserToDelete(null)
        setRequestingOTP(false)
    }

    const handleUserSuccess = () => {
        fetchUsers()
        setEditingUser(null)
    }

    const handleEditUser = (user) => {
        setEditingUser(user)
        setShowUserModal(true)
    }

    const handleAddUser = () => {
        setEditingUser(null)
        setShowUserModal(true)
    }



    const handleCloseModal = () => {
        setShowUserModal(false)
        setEditingUser(null)
    }

    const handleViewUserDetails = (user) => {
        setSelectedUser(user)
        setShowUserDetailsModal(true)
    }

    const handleCloseDetailsModal = () => {
        setShowUserDetailsModal(false)
        setSelectedUser(null)
    }

    const handleViewLocations = (user) => {
        setSelectedUser(user)
        setSelectedUserLocations(user.accessStats?.locations || [])
        setShowLocationModal(true)
    }

    const handleCloseLocationModal = () => {
        setShowLocationModal(false)
        setSelectedUser(null)
        setSelectedUserLocations([])
    }

    // Helper function to format location name - extract and format just the location part
    const formatLocationName = (locationString) => {
        if (!locationString) return locationString
        
        // Split by comma and get the last part (the actual location name)
        const parts = locationString.split(',').map(part => part.trim())
        const locationPart = parts[parts.length - 1]
        
        // Replace underscores with spaces
        let formatted = locationPart.replace(/_/g, ' ')
        
        // Capitalize first letter of each word
        formatted = formatted.split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
        
        return formatted
    }

    if (!hasPermission('users', 'read')) {
        return (
            <div className="text-center py-12">
                <UsersIcon className="h-12 w-12 text-indigo-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
                <p className="text-gray-600">You don't have permission to view users.</p>
            </div>
        )
    }

    return (
        <div className="  max-w-7xl mx-auto px-2 md:px-6 ">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-indigo-700 tracking-tight">Users</h1>
                    <p className="text-gray-600">Manage system users and their roles</p>
                </div>
                <div className="flex gap-3">
                    {hasPermission('users', 'create') && (
                        <button
                            onClick={handleAddUser}
                            className="p-[5px] btn flex items-center gap-2 rounded-lg shadow-md bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white transition"
                        >
                            <Plus className="h-4 w-4" />
                            Add User
                        </button>
                    )}

                </div>
            </div>

            {/* Search and Filters */}
            <div className="bg-white mt-4 rounded-xl shadow p-4">
                <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input pl-10 w-full focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
                            />
                        </div>
                    </div>
                    <button type="submit" className="btn py-2 px-6 rounded-lg shadow bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 text-white transition">
                        Search
                    </button>
                </form>
            </div>

            {/* Users Table */}
            <div className="bg-white mt-4 mb-4 rounded-xl shadow p-4 overflow-x-auto">
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-blue-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                    User
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                    Role
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                    Location Access
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                    Camera Access
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                    Last Login
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {users.map((user) => (
                                <tr key={user._id} className="hover:bg-blue-50 transition">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10">
                                                <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center shadow">
                                                    <span className="text-sm font-bold text-indigo-700">
                                                        {user.username.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-semibold text-gray-900">
                                                    {user.username}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {user.email}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
                                            {user.role?.name}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${user.isActive
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                            }`}>
                                            {user.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center text-sm text-gray-900">
                                            <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                                            <div>
                                                {user.accessStats?.locations?.length > 0 ? (
                                                    <button
                                                        onClick={() => handleViewLocations(user)}
                                                        className="max-w-xs text-left hover:bg-gray-50 rounded p-1 transition-colors"
                                                    >
                                                        <div className="text-sm font-medium text-blue-600 hover:text-blue-800">
                                                            {user.accessStats.locations.length} location{user.accessStats.locations.length !== 1 ? 's' : ''}
                                                        </div>
                                                        <div className="text-xs text-gray-500 truncate" title={user.accessStats.locations.join(', ')}>
                                                            {user.accessStats.locations.slice(0, 2).join(', ')}
                                                            {user.accessStats.locations.length > 2 && `, +${user.accessStats.locations.length - 2} more`}
                                                        </div>
                                                    </button>
                                                ) : (
                                                    <span className="text-gray-400">No access</span>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center text-sm text-gray-900">
                                            <Camera className="h-4 w-4 text-gray-400 mr-2" />
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                                user.accessStats?.totalCameras > 0 
                                                    ? 'bg-blue-100 text-blue-800' 
                                                    : 'bg-gray-100 text-gray-600'
                                            }`}>
                                                {user.accessStats?.totalCameras || 0} camera{user.accessStats?.totalCameras !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {user.lastLogin
                                            ? new Date(user.lastLogin).toLocaleDateString() + ' ' + new Date(user.lastLogin).toLocaleTimeString()
                                            : 'Never'
                                        }
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center justify-end space-x-2">
                                            {hasPermission('users', 'read') && (
                                                <button
                                                    onClick={() => handleViewUserDetails(user)}
                                                    className="text-cyan-600 hover:text-cyan-900"
                                                    title="View user details"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                            )}
                                            {hasPermission('users', 'update') && (
                                                <button
                                                    onClick={() => handleEditUser(user)}
                                                    className="text-cyan-500 hover:text-cyan-700"
                                                    title="Edit user"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                            )}
                                            {hasPermission('users', 'delete') && (
                                                <button
                                                    onClick={() => deleteUser(user._id)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Delete user"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex flex-col md:flex-row items-center justify-between gap-2">
                    <div className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="btn btn-outline"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="btn btn-outline"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* User Deletion Logs */}
            {hasPermission('users', 'read') && (
                <UserDeletionLogs />
            )}

            {/* User Modal */}
            <UserModal
                isOpen={showUserModal}
                onClose={handleCloseModal}
                user={editingUser}
                onSuccess={handleUserSuccess}
                currentUser={currentUser}
            />

            {/* User Details Modal */}
            <UserDetailsModal
                isOpen={showUserDetailsModal}
                onClose={handleCloseDetailsModal}
                user={selectedUser}
            />

            {/* OTP Modal for Super Admin Deletion */}
            {showOTPModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <div className="flex items-center space-x-3">
                                <Shield className="h-5 w-5 text-red-600" />
                                <h2 className="text-lg font-semibold text-gray-900">
                                    Verify Deletion
                                </h2>
                            </div>
                            <button
                                onClick={handleCloseOTPModal}
                                className="text-gray-400 hover:text-gray-600"
                                disabled={otpLoading}
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="p-6">
                            <div className="mb-4">
                                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                                    <Mail className="h-4 w-4" />
                                    <span>OTP has been sent to your email</span>
                                </div>
                                <p className="text-sm text-gray-500 mb-4">
                                    As a security measure, please enter the OTP sent to your email to confirm user deletion.
                                </p>
                            </div>

                            <form onSubmit={handleOTPSubmit}>
                                <div className="mb-4">
                                    <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                                        Enter OTP
                                    </label>
                                    <input
                                        type="text"
                                        id="otp"
                                        value={otp}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                                            setOtp(value)
                                            setOtpError('')
                                        }}
                                        placeholder="000000"
                                        className="input w-full text-center text-2xl tracking-widest font-mono"
                                        maxLength={6}
                                        disabled={otpLoading || requestingOTP}
                                        autoFocus
                                    />
                                    {otpError && (
                                        <p className="mt-2 text-sm text-red-600">{otpError}</p>
                                    )}
                                </div>

                                <div className="flex space-x-3">
                                    <button
                                        type="button"
                                        onClick={handleCloseOTPModal}
                                        disabled={otpLoading || requestingOTP}
                                        className="flex-1 btn btn-outline"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={otpLoading || requestingOTP || otp.length !== 6}
                                        className="flex-1 btn bg-red-600 hover:bg-red-700 text-white"
                                    >
                                        {otpLoading ? 'Deleting...' : 'Delete User'}
                                    </button>
                                </div>
                            </form>

                            {requestingOTP && (
                                <div className="mt-4 text-center">
                                    <div className="inline-flex items-center text-sm text-gray-600">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2"></div>
                                        Sending OTP...
                                    </div>
                                </div>
                            )}

                            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                <p className="text-xs text-yellow-800">
                                    <strong>Warning:</strong> This action is irreversible. The user and all associated data will be permanently deleted.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Location Details Modal */}
            {showLocationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-xl mx-4 max-h-[80vh] flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
                            <div className="flex items-center space-x-2">
                                <MapPin className="h-4 w-4 text-indigo-600" />
                                <h2 className="text-base font-semibold text-gray-900">
                                    Location Access for {selectedUser?.username}
                                </h2>
                            </div>
                            <button
                                onClick={handleCloseLocationModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="p-4 overflow-y-auto flex-1 min-h-0">
                            {selectedUserLocations.length > 0 ? (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-xs font-medium text-gray-700">
                                            {selectedUserLocations.length} Location{selectedUserLocations.length !== 1 ? 's' : ''}
                                        </h3>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 gap-1.5">
                                        {selectedUserLocations.map((location, index) => {
                                            const formattedLocation = formatLocationName(location)
                                            return (
                                                <div
                                                    key={index}
                                                    className="flex items-center p-2 bg-gray-50 rounded border hover:bg-gray-100 transition-colors"
                                                >
                                                    <MapPin className="h-3.5 w-3.5 text-indigo-500 mr-2 flex-shrink-0" />
                                                    <p className="text-xs font-medium text-gray-900">
                                                        {formattedLocation}
                                                    </p>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-6">
                                    <MapPin className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                                    <h3 className="text-sm font-semibold text-gray-900 mb-1">No Location Access</h3>
                                    <p className="text-xs text-gray-600">This user doesn't have access to any locations.</p>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end p-3 border-t border-gray-200 flex-shrink-0">
                            <button
                                onClick={handleCloseLocationModal}
                                className="px-4 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    )
}

export default Users 